import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Form, Input, Row } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

interface AnswerOption {
  id?: string;
  title: string;
  subTitle?: string;
  imageLink?: string;
}

interface Props {
  t: (key: string, opts?: any) => string;
  answerOptions: AnswerOption[];
  onOptionChange: (index: number, field: keyof AnswerOption, value: string) => void;
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
  minAnswers: number;
  maxAnswers: number;
  readOnly?: boolean;
}

const SearchAnswers: React.FC<Props> = ({
  t,
  answerOptions,
  onOptionChange,
  onAddOption,
  onRemoveOption,
  minAnswers,
  maxAnswers,
  readOnly = false,
}) => {
  const [charCounts, setCharCounts] = useState<{ [key: string]: { title: number; subTitle: number } }>({});

  const maxLength = 42;

  const handleCharCountChange = (optionId: string, field: 'title' | 'subTitle', value: string) => {
    setCharCounts(prev => ({
      ...prev,
      [optionId]: {
        ...prev[optionId],
        [field]: value.length,
      },
    }));
  };

  const handleInputChange = (index: number, field: keyof AnswerOption, value: string) => {
    if (field === 'title' || field === 'subTitle') {
      const truncatedValue = value.slice(0, maxLength);
      onOptionChange(index, field, truncatedValue);
      handleCharCountChange(
        answerOptions[index].id || `option-${index}`,
        field as 'title' | 'subTitle',
        truncatedValue,
      );
      return;
    }

    // Do not truncate imageLink to allow full URL
    onOptionChange(index, field, value);
  };

  useEffect(() => {
    const initialCounts: { [key: string]: { title: number; subTitle: number } } = {};
    answerOptions.forEach((option, index) => {
      const optionId = option.id || `option-${index}`;
      initialCounts[optionId] = {
        title: option.title.length,
        subTitle: option.subTitle?.length || 0,
      };
    });
    setCharCounts(initialCounts);
  }, [answerOptions]);

  return (
    <StyledAnswerSection>
      <Row gutter={[16, 8]}>
        {answerOptions.map((option, index) => {
          const optionId = option.id || `option-${index}`;
          const currentCounts = charCounts[optionId] || { title: 0, subTitle: 0 };

          return (
            <Col span={24} key={option.id}>
              <StyledAnswerCard>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label={`${t('questionForm.labels.answerTitle')} ${index + 1}`} required={index < 2}>
                      <div className="position-relative">
                        <Input
                          value={option.title}
                          onChange={e => handleInputChange(index, 'title', e.target.value)}
                          placeholder={t('questionForm.placeholders.answerOption')}
                          maxLength={maxLength}
                          disabled={readOnly}
                        />
                        <div className="count-item">{`${currentCounts.title}/${maxLength}`}</div>
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={`${t('questionForm.labels.answerSubtitle')} ${index + 1}`}>
                      <div className="position-relative">
                        <Input
                          value={option.subTitle}
                          onChange={e => handleInputChange(index, 'subTitle', e.target.value)}
                          placeholder={t('questionForm.placeholders.title')}
                          maxLength={maxLength}
                          disabled={readOnly}
                        />
                        <div className="count-item">{`${currentCounts.subTitle}/${maxLength}`}</div>
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item label={t('questionForm.labels.imageUrl')}>
                      <Input
                        value={option.imageLink}
                        onChange={e => handleInputChange(index, 'imageLink', e.target.value)}
                        placeholder={t('questionForm.placeholders.imageUrl')}
                        disabled={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Form.Item label=" ">
                      {answerOptions.length > minAnswers && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => onRemoveOption(index)}
                          disabled={readOnly}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </StyledAnswerCard>
            </Col>
          );
        })}
      </Row>

      {answerOptions.length < maxAnswers && (
        <Button type="dashed" icon={<PlusOutlined />} onClick={onAddOption} block disabled={readOnly}>
          {t('questionForm.buttons.addRow')}
        </Button>
      )}
    </StyledAnswerSection>
  );
};

const StyledAnswerSection = styled.div`
  margin-bottom: 24px;

  .position-relative {
    position: relative;
  }

  .count-item {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #cbcbcb;
    pointer-events: none;
  }

  .position-relative input {
    padding-right: 40px;
  }
`;

const StyledAnswerCard = styled(Card)`
  border: none;

  .ant-card-body {
    padding: 0 16px;
  }
`;

export default SearchAnswers;
